import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { QrCodeIcon, RotateCwIcon, SparklesIcon } from 'lucide-react';
import { petReminders, sourceLabels, type Pet } from '../../data/animaux';
interface PetCardProps {
  pet: Pet;
  lost: boolean;
}
export function PetCard({
  pet,
  lost
}: PetCardProps) {
  const [flipped, setFlipped] = useState(false);
  const reminders = petReminders[pet.id] ?? [];
  return <div data-id="element-200" data-mp="3ghzwtr5mo6ic">
      <div className="[perspective:1600px]" data-id="element-201" data-mp="3gihyx4ez1ey6">
        <motion.div animate={{
        rotateY: flipped ? 180 : 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative h-[420px] w-full [transform-style:preserve-3d]" data-id="element-202" data-mp="3lg1dy6jhtvr6">
          {/* Front */}
          <div className="absolute inset-0 overflow-hidden rounded-3xl bg-ville-navy text-white shadow-2xl shadow-ville-navy/25 [backface-visibility:hidden]" data-id="element-203" data-mp="3u94x8vgtl2tq">
            <div className="flex h-full" data-id="element-204" data-mp="31rsm5xlv3rs7">
              <img src={pet.photo} alt={pet.name} className="h-full w-2/5 object-cover" data-id="element-205" data-mp="31qhh7ugokdzi" />
              <div className="flex flex-1 flex-col p-6" data-id="element-206" data-mp="jv1s4rxvl9vq">
                <div className="flex items-start justify-between gap-3" data-id="element-207" data-mp="exidvs16gx3s">
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/60" data-id="element-208" data-mp="ezcyf58jen7c">
                    Pet card
                  </span>
                  {lost ? <span className="rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-id="element-209" data-mp="56nr8mk0rvht">
                      Missing
                    </span> : <span className="rounded-full bg-ville-lime px-3 py-1 text-xs font-bold text-ville-navy" data-id="element-210" data-mp="3sc6cf6lnjt3w">
                      Licence valid
                    </span>}
                </div>

                <p className="mt-5 text-3xl font-extrabold tracking-tight" data-id="element-211" data-mp="3idyxetaxbl4j">{pet.name}</p>
                <p className="text-sm text-white/70" data-id="element-212" data-mp="3igbobvynt5tq">
                  {pet.breed} · {pet.age}
                </p>

                <dl className="mt-6 space-y-2 text-sm" data-id="element-213" data-mp="1gmm3ezahuxn9">
                  <div className="flex justify-between gap-3" data-id="element-214" data-mp="1gn5cdqi62bp5">
                    <dt className="text-white/55" data-id="element-215" data-mp="3b2kj9nd5o59r">Born</dt>
                    <dd className="font-semibold" data-id="element-216" data-mp="3b0zpw12kusdh">{pet.birthDate}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-217" data-mp="3fvnh47oyu0vr">
                    <dt className="text-white/55" data-id="element-218" data-mp="315zdv0x84s5e">Licence</dt>
                    <dd className="font-mono tracking-widest" data-id="element-219" data-mp="363hocvqw3ffi">{pet.medal}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-220" data-mp="3ttlj0dm3o4ud">
                    <dt className="text-white/55" data-id="element-221" data-mp="3tr91u2o61yid">Valid until</dt>
                    <dd className="font-semibold" data-id="element-222" data-mp="2lzfqm3am4e1">{pet.validUntil}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-223" data-mp="3ju4dpxarfsyx">
                    <dt className="text-white/55" data-id="element-224" data-mp="3ou8y3yesh7uf">Owner</dt>
                    <dd className="font-semibold" data-id="element-225" data-mp="3oso2s6knhkvi">{pet.owner}</dd>
                  </div>
                </dl>

                <div className="mt-auto flex items-end justify-between" data-id="element-226" data-mp="28i3ka52ybgcu">
                  <span className="text-xs font-semibold text-white/60" data-id="element-227" data-mp="2dfv9usg4iqdv">Town of Bonnyville</span>
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-white/10" aria-hidden="true" data-id="element-228" data-mp="vg5qe2kjc79r">
                    <QrCodeIcon className="h-7 w-7 text-white" data-id="element-229" data-mp="qhuk21tg8ewa" />
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Back */}
          <div className="absolute inset-0 overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-xl [backface-visibility:hidden] [transform:rotateY(180deg)]" data-id="element-230" data-mp="10hv2lljhuqzf">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ville-indigo" data-id="element-231" data-mp="7snupvy5gp1i">Good to know</p>
            <h3 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-232" data-mp="lkb1wqtmrhz2">{pet.name}</h3>

            <ul className="mt-5 space-y-3" data-id="element-233" data-mp="grhwe0qc85lx">
              {reminders.map(reminder => <li key={reminder.id} className="rounded-2xl bg-slate-50 px-4 py-3" data-id="element-234" data-mp="3p7ulv6sccv4v">
                  <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-235" data-mp="3l6raoxl8nn9z">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-236" data-mp="3l4y7js9u3l6q">{reminder.label}</p>
                    <p className="text-sm text-slate-600" data-id="element-237" data-mp="1ojbiln0uevv">{reminder.date}</p>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500" data-id="element-238" data-mp="6ncn28iqzvte">{sourceLabels[reminder.source]}</p>
                </li>)}
              <li className="rounded-2xl bg-slate-50 px-4 py-3" data-id="element-239" data-mp="gfig7lkqkja6">
                <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-240" data-mp="ggthneqpley4">
                  <p className="text-sm font-bold text-ville-navy" data-id="element-241" data-mp="le1zfe7y3gyu">Microchip</p>
                  <p className="font-mono text-sm text-slate-600" data-id="element-242" data-mp="pf5hm7dgtp38">{pet.microchip}</p>
                </div>
              </li>
            </ul>

            <p className="mt-5 flex items-center gap-2 text-sm font-bold text-ville-indigo" data-id="element-243" data-mp="ud6wsh7rt49f">
              <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-244" data-mp="zca4k9f4xol3" />
              Ask ATNA
            </p>
            <p className="mt-1 text-xs leading-relaxed text-slate-500" data-id="element-245" data-mp="2w8zhgpmw4jxt">
              bciti+ is not your pet’s official medical record. The information comes from your reminders, from the Town or from your authorized clinic.
            </p>
          </div>
        </motion.div>
      </div>

      <button type="button" onClick={() => setFlipped(v => !v)} className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-246" data-mp="2ou6xg1h92cqu">
        <RotateCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-247" data-mp="37n16kes6zan5" />
        {flipped ? 'View the card' : 'Flip the card'}
      </button>
    </div>;
}